/**
 * Pure decision logic for reconciling a pre-login marketing-consent choice into
 * the customer's `metadata.health_consent` profile field (MWP-1023).
 *
 * Kept out of the `'use server'` customer module so the compliance-critical
 * decision table can be unit-tested without a browser, network, or Server
 * Action machinery (a `'use server'` module may only export async functions).
 */

/**
 * Normalise a raw `metadata.health_consent` value into a tri-state.
 *
 * Medusa stores metadata as JSONB, so a boolean written by
 * `updateHealthConsent` round-trips as a real boolean; we tolerate the string
 * forms defensively. The distinction that matters is **`undefined` (never set)
 * vs `false` (explicit withdrawal)**.
 */
export function normalizeHealthConsent(value: unknown): boolean | undefined {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  return undefined;
}

/**
 * Derive this device's marketing-consent choice from stored preferences for the
 * login reconcile.
 *
 * `getConsentPreferences` casts parsed localStorage to `ConsentPreferences`
 * without runtime validation, so legacy or malformed data may carry a
 * non-boolean (or absent) `advertising`. An indeterminate choice must NOT touch
 * the Article-9 profile field — it is **neither a grant nor a withdrawal**.
 * Treating `undefined` as `false` would fabricate a withdrawal; treating it as
 * `true` would fabricate health-data consent that was never given. Both are
 * wrong, so we no-op.
 *
 * @returns the boolean choice, or `null` when it cannot be determined (no prefs,
 * or `advertising` is not a boolean).
 */
export function deviceMarketingConsent(
  prefs: { advertising?: unknown } | null,
): boolean | null {
  if (!prefs || typeof prefs.advertising !== 'boolean') {
    return null;
  }
  return prefs.advertising;
}

/**
 * Decide what (if anything) to write to `metadata.health_consent` when a user
 * logs in, given this device's stored advertising consent and the profile's
 * current value.
 *
 * Rule (confirmed with the consent-spec owner): **an explicit withdrawal must
 * never be silently overturned by a stale grant from another device.**
 *
 * | device (localStorage) | profile (metadata)  | result           |
 * | --------------------- | ------------------- | ---------------- |
 * | `false`               | not `false`         | write `false`    |
 * | `false`               | `false`             | no-op            |
 * | `true`                | `undefined` (unset) | write `true`     |
 * | `true`                | `true`              | no-op            |
 * | `true`                | `false` (revoked)   | no-op ← sticks   |
 *
 * @returns the boolean to persist, or `null` when the profile already matches
 * the device / an explicit withdrawal must be preserved.
 */
export function resolveHealthConsentReconcile(
  deviceAdvertisingConsent: boolean,
  profileHealthConsent: boolean | undefined,
): boolean | null {
  if (deviceAdvertisingConsent === false) {
    // Withdrawal always propagates, unless the profile already records it.
    return profileHealthConsent === false ? null : false;
  }

  // Device grants marketing consent. Only fill an *unset* profile — this is the
  // case the ticket fixes (a consented user whose profile was never written).
  // An explicit `false` is a recorded withdrawal and is never resurrected here.
  return profileHealthConsent === undefined ? true : null;
}
